/** Business display timezone for enquiries, reports, logs, and trails. */
export const IST_TIME_ZONE = "Asia/Kolkata";

/**
 * Parse API datetimes. Naive ISO strings from the backend are treated as UTC.
 * @param {string|Date|number|null|undefined} value
 * @returns {Date|null}
 */
export function parseUtcDateLike(value) {
  if (value == null || value === "") return null;
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }
  if (typeof value === "number") {
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  const raw = String(value).trim();
  if (!raw) return null;
  const needsUtcSuffix = !/[zZ]$|[+-]\d{2}:\d{2}$/.test(raw);
  const normalized = needsUtcSuffix
    ? raw.includes("T")
      ? `${raw}Z`
      : `${raw.replace(" ", "T")}Z`
    : raw;
  const d = new Date(normalized);
  return Number.isNaN(d.getTime()) ? null : d;
}

/**
 * Format a UTC (or naive-UTC) datetime for display in IST.
 * @param {string|Date|number|null|undefined} value
 * @param {Intl.DateTimeFormatOptions} [options]
 */
export function formatDateTimeIST(value, options = {}) {
  const d = parseUtcDateLike(value);
  if (!d) return "—";
  return d.toLocaleString("en-GB", {
    timeZone: IST_TIME_ZONE,
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
    ...options,
  });
}

/**
 * Date-only in IST (e.g. dashboard / trail date labels).
 * If value is already YYYY-MM-DD (IST calendar date from SQL), format without shifting.
 */
export function formatDateIST(value, options = {}) {
  if (value == null || value === "") return "—";
  const raw = String(value).trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) {
    const [y, m, d] = raw.split("-").map(Number);
    const label = new Date(Date.UTC(y, m - 1, d));
    return label.toLocaleDateString("en-GB", {
      timeZone: "UTC",
      day: "2-digit",
      month: "short",
      year: "numeric",
      ...options,
    });
  }
  const d = parseUtcDateLike(value);
  if (!d) return "—";
  return d.toLocaleDateString("en-GB", {
    timeZone: IST_TIME_ZONE,
    day: "2-digit",
    month: "short",
    year: "numeric",
    ...options,
  });
}

/**
 * Current calendar date in IST as YYYY-MM-DD.
 */
export function getIstTodayYmd() {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: IST_TIME_ZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(new Date());
}

/** Add days to a YYYY-MM-DD string (calendar arithmetic). */
export function addDaysYmd(ymd, days) {
  const [y, m, d] = String(ymd).split("-").map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d + days));
  const yy = dt.getUTCFullYear();
  const mm = String(dt.getUTCMonth() + 1).padStart(2, "0");
  const dd = String(dt.getUTCDate()).padStart(2, "0");
  return `${yy}-${mm}-${dd}`;
}

function mondayOfIstWeek(ymd) {
  const [y, m, d] = String(ymd).split("-").map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  const dow = dt.getUTCDay();
  const back = dow === 0 ? 6 : dow - 1;
  return addDaysYmd(ymd, -back);
}

/**
 * Inclusive IST calendar start/end (YYYY-MM-DD) for dashboard conversion/enquiry ranges.
 */
export function getIstDateRangeYmd(range, customStart, customEnd) {
  const today = getIstTodayYmd();
  let start = today;
  let end = today;

  switch (range) {
    case "today":
      break;
    case "yesterday":
      start = end = addDaysYmd(today, -1);
      break;
    case "this_week":
      start = mondayOfIstWeek(today);
      end = addDaysYmd(start, 6);
      break;
    case "last_week": {
      const thisMon = mondayOfIstWeek(today);
      start = addDaysYmd(thisMon, -7);
      end = addDaysYmd(start, 6);
      break;
    }
    case "this_month": {
      const [y, m] = today.split("-").map(Number);
      start = `${y}-${String(m).padStart(2, "0")}-01`;
      const ny = m === 12 ? y + 1 : y;
      const nm = m === 12 ? 1 : m + 1;
      end = addDaysYmd(`${ny}-${String(nm).padStart(2, "0")}-01`, -1);
      break;
    }
    case "last_month": {
      const [y, m] = today.split("-").map(Number);
      const ly = m === 1 ? y - 1 : y;
      const lm = m === 1 ? 12 : m - 1;
      start = `${ly}-${String(lm).padStart(2, "0")}-01`;
      end = addDaysYmd(`${y}-${String(m).padStart(2, "0")}-01`, -1);
      break;
    }
    case "this_year": {
      const y = Number(today.slice(0, 4));
      start = `${y}-01-01`;
      end = `${y}-12-31`;
      break;
    }
    case "last_year": {
      const y = Number(today.slice(0, 4)) - 1;
      start = `${y}-01-01`;
      end = `${y}-12-31`;
      break;
    }
    case "custom":
      if (customStart && customEnd) {
        start = String(customStart).slice(0, 10);
        end = String(customEnd).slice(0, 10);
      } else {
        return null;
      }
      break;
    default:
      start = addDaysYmd(today, -7);
      end = today;
  }

  if (!start || !end) return null;
  return { start, end };
}

/**
 * Rich datetime used on enquiry trail / status log.
 */
export function formatTrailDateTimeIST(value) {
  if (!value) return "Pending";
  const d = parseUtcDateLike(value);
  if (!d) return "Pending";
  return d.toLocaleString("en-US", {
    timeZone: IST_TIME_ZONE,
    weekday: "short",
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
}

/** True when a report cell looks like an ISO / SQL datetime string. */
export function looksLikeDateTimeValue(value) {
  if (value instanceof Date) return true;
  if (typeof value !== "string") return false;
  const s = value.trim();
  if (!s) return false;
  return (
    /^\d{4}-\d{2}-\d{2}/.test(s) &&
    (s.includes("T") || s.includes(" ") || s.length === 10)
  );
}

/**
 * Format report cell values: datetimes → IST, leave other values alone.
 */
export function formatReportCellValue(value) {
  if (value === undefined || value === null) return "";
  if (typeof value === "object" && !(value instanceof Date)) {
    return JSON.stringify(value);
  }
  if (value instanceof Date || looksLikeDateTimeValue(value)) {
    const s = String(value);
    if (/^\d{4}-\d{2}-\d{2}$/.test(s.trim())) {
      return formatDateIST(s);
    }
    return formatDateTimeIST(value);
  }
  return String(value);
}
